import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Check for existing session on mount (cookie is sent automatically)
  useEffect(() => {
    api.get('/auth/me')
      .then(userData => {
        setUser(userData);
        setAuthError(null);
        setLoading(false);
      })
      .catch((err) => {
        setUser(null);

        // Distinguish network errors from auth failures (401).
        // A 401 is expected when not logged in -- just show login page.
        // A network/server error means we couldn't reach the API at all.
        if (err.status === 401 || err.status === 403) {
          setAuthError(null);
        } else {
          setAuthError('Unable to connect to the server. Please check your connection and try again.');
        }

        setLoading(false);
      });
  }, []);

  const login = useCallback((_token, userData) => {
    // Token is in HttpOnly cookie -- no need to store anywhere.
    // This is called from AuthCallback to update UI state.
    setUser(userData);
    setAuthError(null);
  }, []);

  // Re-fetch current user data from the server (e.g., after onboarding dismiss)
  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.get('/auth/me');
      setUser(userData);
    } catch {
      // If refresh fails, keep existing user data
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors -- we're logging out anyway
    }
    setUser(null);
  }, []);

  // Start impersonating a user (admin only). Calls the server to issue a new
  // JWT with impersonation context, then refreshes the client state.
  const startImpersonating = useCallback(async (targetUserId) => {
    await api.post(`/auth/impersonate/${targetUserId}`);
    await refreshUser();
  }, [refreshUser]);

  // Stop impersonation and return to admin identity
  const stopImpersonating = useCallback(async () => {
    await api.post('/auth/stop-impersonate');
    await refreshUser();
  }, [refreshUser]);

  const isImpersonating = !!user?.is_impersonating;

  const value = {
    user, loading, authError, login, logout, refreshUser,
    isAuthenticated: !!user, isImpersonating,
    startImpersonating, stopImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
